import React, { useEffect, useMemo, useState } from 'react';
import { Col, Container, Form } from 'react-bootstrap';
import { Link, useSearchParams } from 'react-router-dom';
import InventoryNav from './InventoryNav';
import FileInventoryExplorer from './FileInventoryExplorer';
import {
  adminFileInventoryDataSource,
  getInventorySites,
  getSiteInventoryDetail,
  type SiteInventoryDto,
  type SiteScope,
} from '@/api/sharepointInventoryApi';
import { usePageTitle } from '@/hooks/usePageTitle';

const FileExplorerPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const siteParam = searchParams.get('siteCollectionId');
  const listParam = searchParams.get('listId');
  const scopedSiteId = siteParam ? Number(siteParam) : undefined;

  const [siteScope, setSiteScope] = useState<SiteScope>('SharePoint');
  const [siteOptions, setSiteOptions] = useState<SiteInventoryDto[]>([]);

  const scopedSite = useMemo(
    () => siteOptions.find((s) => s.siteCollectionId === scopedSiteId) ?? null,
    [siteOptions, scopedSiteId]
  );

  usePageTitle(scopedSite ? `${scopedSite.title || scopedSite.url} - File Explorer` : null, 'File Explorer');

  useEffect(() => {
    getInventorySites({ siteScope, take: 500, sortBy: 'title', sortDirection: 'asc' })
      .then((r) => setSiteOptions(r.items))
      .catch(() => setSiteOptions([]));
  }, [siteScope]);

  // The picker only holds the first page of sites, so a scoped site outside it would leave the
  // dropdown blank and hide which site is selected.
  useEffect(() => {
    if (!scopedSiteId) return;
    if (siteOptions.some((s) => s.siteCollectionId === scopedSiteId)) return;

    getSiteInventoryDetail(scopedSiteId)
      .then((detail) =>
        setSiteOptions((prev) =>
          prev.some((s) => s.siteCollectionId === detail.site.siteCollectionId)
            ? prev
            : [detail.site, ...prev]
        )
      )
      .catch(() => undefined);
  }, [scopedSiteId, siteOptions]);

  const changeSite = (value: string) => {
    const next = new URLSearchParams(searchParams);
    next.delete('listId');
    if (value) next.set('siteCollectionId', value);
    else next.delete('siteCollectionId');
    setSearchParams(next);
  };

  return (
    <Container fluid className="py-3">
      <div className="mb-3">
        {scopedSite ? (
          <Link to={`/admin/sharepoint/inventory/sites/${scopedSite.siteCollectionId}`} className="small">
            &larr; Back to {scopedSite.title || scopedSite.url}
          </Link>
        ) : (
          <Link to="/admin/sharepoint/inventory" className="small">&larr; Back to inventory</Link>
        )}
        <h3 className="mb-1 mt-2">File Explorer</h3>
        <div className="text-muted small">
          {scopedSite
            ? `Files in ${scopedSite.title || scopedSite.url}.`
            : 'All inventoried files across every scanned site.'}{' '}
          Latest observed file metadata &mdash; document libraries only.
        </div>
      </div>

      <InventoryNav />

      <FileInventoryExplorer
        dataSource={adminFileInventoryDataSource}
        siteScope={siteScope}
        siteCollectionId={scopedSiteId}
        listId={listParam ?? undefined}
        siteLink={(f) => `/admin/sharepoint/inventory/sites/${f.siteCollectionId}`}
        scopeControls={
          <>
            <Col md={2}>
              <Form.Label className="small mb-1">Scope</Form.Label>
              <Form.Select value={siteScope} onChange={(e) => setSiteScope(e.target.value as SiteScope)}>
                <option value="SharePoint">SharePoint only</option>
                <option value="OneDrive">OneDrive only</option>
                <option value="All">SharePoint + OneDrive</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label className="small mb-1">Site</Form.Label>
              <Form.Select value={siteParam ?? ''} onChange={(e) => changeSite(e.target.value)}>
                <option value="">All sites</option>
                {siteOptions.map((s) => (
                  <option key={s.siteCollectionId} value={s.siteCollectionId}>
                    {s.title || s.url}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </>
        }
      />
    </Container>
  );
};

export default FileExplorerPage;
